<script setup lang="ts">

import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import MediumPlusModel from '@/components/Interface/Modal/ModelSizes/MediumPlusModel.vue'
import ConfigPQAAvailable from '@/components/Interface/Modal/Modals/ModalConfigPQA/ConfigPQAAvailable.vue'
import { computed, onMounted, ref } from 'vue'
import { useAssetStore } from '@/stores/assetStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { usePQAStore } from '@/stores/PQAStore.ts'
import ConfigPQASelected from '@/components/Interface/Modal/Modals/ModalConfigPQA/ConfigPQASelected.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import TagButton from '@/components/Elements/Buttons/TagButton.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Props
 */

/**
 * Stores / models
 */
const gameStore = useGameStore()
const PQAStore = usePQAStore()
const assetStore = useAssetStore()

/**
 * Variables
 */
const type = ref('')
const list = [
  'food','production','science','credit','energy','energy_storage',
  'ore_storage','scanning_range','defence_attack_ships','defence_shields',
  'ore_refinery','ore'
];

/**
 * Computed
 */
const availableBuildings = computed(() => {
  let filtered: Map<any, any>;

  if (type.value === '') {
    filtered = assetStore.findAllBuildingProductionObjects();
  } else {
    filtered = assetStore.findBuildingProductionObjectsByEffectType(type.value);
  }

  // Sort by production cost
  return new Map([...filtered.entries()].sort((a, b) => a[1].productionCost - b[1].productionCost));
});


/**
 * Methods
 */
const clearQueue = () => {
  const count = PQAStore.selectedQueueData.length
  PQAStore.clearQueue()
  gameStore.setTinySuccessToastMessage(t('modals.pqa.config.messages.queueCleared', { count }))
}

const exportQueue = () => {
  try {
    // Get building codes from store
    const buildingCodes = PQAStore.exportQueueToBuildingCodes()

    // Create the data object to export
    const exportData = {
      version: '1.0',
      buildingCodes: buildingCodes,
      exportedAt: new Date().toISOString()
    }

    // Convert to JSON
    const jsonString = JSON.stringify(exportData, null, 2)

    // Create a blob and download
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'buildingqueue.ferion'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    // Success toast
    gameStore.setTinySuccessToastMessage(t('modals.pqa.config.messages.exportSuccess', { count: buildingCodes.length }))
  } catch (error) {
    gameStore.setTinyErrorToastMessage(t('modals.pqa.config.messages.exportFailed'))
  }
}

const importQueue = () => {
  // Create a hidden file input
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.ferion'

  input.onchange = (e: Event) => {
    const target = e.target as HTMLInputElement
    const file = target.files?.[0]

    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string
        const importData = JSON.parse(content)

        // Validate the data structure
        if (importData.buildingCodes && Array.isArray(importData.buildingCodes)) {
          // Import using store method
          const result = PQAStore.importQueueFromBuildingCodes(importData.buildingCodes)

          // Show success/warning toast
          if (result.missing > 0) {
            gameStore.setTinyErrorToastMessage(t('modals.pqa.config.messages.importIncomplete', { count: result.missing }))
          } else {
            gameStore.setTinySuccessToastMessage(t('modals.pqa.config.messages.importSuccess', { count: result.success }))
          }
        } else {
          gameStore.setTinyErrorToastMessage(t('modals.pqa.config.messages.importInvalidFormat'))
        }
      } catch (error) {
        gameStore.setTinyErrorToastMessage(t('modals.pqa.config.messages.importFailed'))
      }
    }

    reader.readAsText(file)
  }

  input.click()
}

/**
 * Lifecycle
 */
onMounted(() => {
  PQAStore.selectQueue(gameStore.modalData)
})
</script>

<template>
  <MediumPlusModel :title="t('modals.pqa.config.title')">
    <div class="text-sm font-medium text-center border-b text-metal-400 border-metal-700 sm:hidden">
      <div class="overflow-x-auto whitespace-nowrap no-scrollbar">
        <ul class="flex">
          <li class="me-2">
            <a href="#"
               :class="{
                 'active-blue': PQAStore.selectedModalMobileTab === 'queue',
                 'text-blue-500 border-blue-500': PQAStore.selectedModalMobileTab === 'queue',
                 'text-metal-500 border-transparent': PQAStore.selectedModalMobileTab !== 'queue',
                 'inline-block p-4 border-b-2 rounded-t-lg  hover:border-metal-300 hover:text-metal-300': true
             }"
               @click="PQAStore.selectedModalMobileTab = 'queue'">
              {{ t('modals.pqa.config.tabs.queue') }}
            </a>
          </li>
          <li class="me-2">
            <a href="#"
               :class="{
                 'active-blue': PQAStore.selectedModalMobileTab === 'available',
                 'text-blue-500 border-blue-500': PQAStore.selectedModalMobileTab === 'available',
                 'text-metal-500 border-transparent': PQAStore.selectedModalMobileTab !== 'available',
                 'inline-block p-4 border-b-2 rounded-t-lg hover:border-metal-300 hover:text-metal-300': true
             }"
               @click="PQAStore.selectedModalMobileTab = 'available'">
              {{ t('modals.pqa.config.tabs.available') }}
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div class="flex justify-between p-2">
      <div :class="{
        'pr-0 md:pr-2 w-full md:block': true,
        'hidden' : PQAStore.selectedModalMobileTab === 'available'
      }">
        <StandardCard :title="t('modals.pqa.config.queue.title')" :help="t('modals.pqa.config.queue.help')" top-padding="0" bottom-padding="0"></StandardCard>
        <div class="flex">
          <div class="pt-1 flex flex-col w-10 me-1">
            <ActionButton @click="clearQueue()" class="mb-1">
              <GenericIcon type="trash" :tooltip="t('modals.pqa.config.tooltips.clear')"/>
            </ActionButton>
            <ActionButton @click="exportQueue()" class="mb-1">
              <GenericIcon type="export" :tooltip="t('modals.pqa.config.tooltips.export')"/>
            </ActionButton>
            <ActionButton @click="importQueue()">
              <GenericIcon type="import" :tooltip="t('modals.pqa.config.tooltips.import')"/>
            </ActionButton>
          </div>
          <div class="scroll-height w-full">
            <template
              v-for="(productionObjectID,index) in PQAStore.selectedQueueData" :key="index"
            >
              <ConfigPQASelected
                :productionObjectId="productionObjectID"
                :PQAId="gameStore.modalData"
                :index="index"/>
            </template>
          </div>
        </div>
      </div>
      <div :class="{
        'w-full md:block': true,
        'hidden' : PQAStore.selectedModalMobileTab === 'queue'
      }">
        <StandardCard :title="t('modals.pqa.config.available.title')" :help="t('modals.pqa.config.available.help')" top-padding="0" bottom-padding="0"></StandardCard>
        <div class="flex">
          <div class="pt-1 flex flex-col w-14 me-1">
            <TagButton
              @click="type = ''"
              :selected="type === ''"
              class="mb-1 !mr-0 text-xs"
            >
              {{ t('modals.pqa.config.all') }}
            </TagButton>
            <template v-for="item in list" :key="item">
              <TagButton
                @click="type = item"
                :selected="type === item"
                class="mb-1 !mr-0"
              >
                <GenericIcon :type="item" :class="{'slatescale' : type !== item}"/>
              </TagButton>
            </template>
          </div>
        <div class="scroll-height w-full">
          <template
            v-for="productionObject in Array.from(availableBuildings.values())"
            :key="productionObject.id"
          >
            <ConfigPQAAvailable
              :productionObjectId="productionObject.id"
              :PQAId="gameStore.modalData"
            />
          </template>
        </div>
        </div>
      </div>
    </div>
  </MediumPlusModel>
</template>

<style scoped lang="scss">
.active-blue {
  background: linear-gradient(
    to top,
    rgba(59, 130, 246, 0.3) 0%,
    rgba(59, 130, 246, 0) 70%
  );
}
.scroll-height {
  height: calc(100vh - 13rem);
  overflow-y: auto;
}
</style>